import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import {
  SwapStatusContainer,
  SwapStatusHeader,
  SwapStatusChip,
  SwapStatusAvatar,
  SwapStatusTitle,
  SwapStatusDescription,
  LoadingContainer,
  SwapStatusRightContainer,
} from './ShiftSwapStatus.styles';
import { selectShiftSwapRequestStatus } from '../../../../redux/shifts/shiftSwapSlice';

/**
 * ShiftSwapStatus component shows pending shift swap status
 * Displays if a shift swap request is pending for this shift
 */
const ShiftSwapStatus = ({ shift }) => {
  const currentUser = useSelector((state) => state.currentUser);
  const swapRequestStatus = useSelector(selectShiftSwapRequestStatus);
  const [targetEmployee, setTargetEmployee] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Check if this shift has a pending swap
  const hasPendingSwap = !!shift?.pendingSwap;

  // Fetch swap request details when pendingSwap changes
  useEffect(() => {
    if (!hasPendingSwap || !shift.pendingSwap) {
      setTargetEmployee(null);
      return;
    }

    // If pendingSwap is "pending", it's an optimistic update
    if (shift.pendingSwap === 'pending') {
      // Show loading while request is in progress, Once request is succeeded, show the pending status immediately
      if (swapRequestStatus === 'pending') {
        setIsLoading(true);
      } else {
        setIsLoading(false);
      }

      // Set target employee to the one selected in the modal
      const selectedEmployee = shift.selectedEmployee;
      if (selectedEmployee) {
        setTargetEmployee(selectedEmployee);
      } else {
        setTargetEmployee(null);
      }
      return;
    }

    const fetchSwapRequestDetails = async () => {
      setIsLoading(true);
      try {
        // Fetch the swap request document
        const swapRequestDoc = await getDoc(doc(db, 'shiftSwapRequests', shift.pendingSwap));
        if (swapRequestDoc.exists()) {
          const swapData = { id: swapRequestDoc.id, ...swapRequestDoc.data() };

          // Fetch target employee details if available
          if (swapData.target) {
            const employeeDoc = await getDoc(doc(db, 'employees', swapData.target));
            if (employeeDoc.exists()) {
              setTargetEmployee({ id: employeeDoc.id, ...employeeDoc.data() });
              setIsLoading(false);
              return;
            }
          }

          // If we couldn't get employee details, use the swap request data
          setTargetEmployee({ id: swapRequestDoc.id, ...swapRequestDoc.data() });
        }
      } catch (error) {
        console.error('Error fetching swap request details:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSwapRequestDetails();
  }, [hasPendingSwap, shift?.pendingSwap, shift?.selectedEmployee, swapRequestStatus]);

  if (!shift || !hasPendingSwap) {
    return null;
  }

  // Determine if the current user is the one who initiated the swap
  const isCurrentUserSource = shift?.eid === currentUser?.employeeInfo?.id;
  const targetName = targetEmployee?.displayName || targetEmployee?.name || 'another employee';
  const targetAvatar = targetEmployee?.profileImageUrl;

  if (isLoading) {
    return (
      <LoadingContainer>
        <Typography variant="body2" color="text.secondary">
          {shift.pendingSwap === 'pending' ? 'Sending swap request...' : 'Loading swap details...'}
        </Typography>
      </LoadingContainer>
    );
  }

  return (
    <SwapStatusContainer>
      <SwapStatusHeader>
        <div>
          <SwapStatusTitle>
            {isCurrentUserSource ? `Request sent to ${targetName}` : `Swap request pending`}
          </SwapStatusTitle>
          <SwapStatusDescription>
            {isCurrentUserSource
              ? `${targetName} will be notified to accept or decline your request`
              : 'A swap request is pending for this shift'}
          </SwapStatusDescription>
        </div>

        <SwapStatusRightContainer>
          {targetEmployee && (
            <SwapStatusAvatar src={targetAvatar} alt={targetName}>
              <PersonIcon />
            </SwapStatusAvatar>
          )}
          <SwapStatusChip label="Swap Pending" color="info" size="small" />
        </SwapStatusRightContainer>
      </SwapStatusHeader>
    </SwapStatusContainer>
  );
};

export default ShiftSwapStatus;

ShiftSwapStatus.propTypes = {
  shift: PropTypes.shape({
    id: PropTypes.string.isRequired,
    pendingSwap: PropTypes.string,
    eid: PropTypes.string,
    vid: PropTypes.string,
  }).isRequired,
};
